import { createTheme, type ThemeOptions } from '@mui/material/styles';

export const tokens = {
  color: {
    bg:           '#0a0a0f',
    surface:      '#111118',
    surface2:     '#1a1a24',
    border:       '#2a2a3a',
    accent:       '#6366f1',
    accentHover:  '#818cf8',
    accentMuted:  'rgba(99, 102, 241, 0.12)',
    success:      '#10b981',
    successMuted: 'rgba(16, 185, 129, 0.12)',
    warning:      '#f59e0b',
    warningMuted: 'rgba(245, 158, 11, 0.12)',
    error:        '#ef4444',
    errorMuted:   'rgba(239, 68, 68, 0.12)',
    info:         '#3b82f6',
    textPrimary:   '#f1f5f9',
    textSecondary: '#94a3b8',
    textMuted:     '#475569',
  },
  font: {
    sans: '"Inter", "Helvetica", "Arial", sans-serif',
    mono: '"JetBrains Mono", "Menlo", monospace',
  },
  radius: {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
  },
  shadow: {
    border: '0 0 0 1px #2a2a3a',
    float:  '0 4px 24px rgba(0,0,0,0.4)',
  },
} as const;

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary:   { main: tokens.color.accent, light: tokens.color.accentHover, dark: '#4f46e5' },
    secondary: { main: tokens.color.accent },
    success:   { main: tokens.color.success },
    warning:   { main: tokens.color.warning },
    error:     { main: tokens.color.error },
    info:      { main: tokens.color.info },
    background: {
      default: tokens.color.bg,
      paper:   tokens.color.surface,
    },
    text: {
      primary:   tokens.color.textPrimary,
      secondary: tokens.color.textSecondary,
      disabled:  tokens.color.textMuted,
    },
    divider: tokens.color.border,
  },
  typography: {
    fontFamily: tokens.font.sans,
    h1: { fontSize: '36px', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 },
    h2: { fontSize: '28px', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '20px', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '17px', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '15px', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '14px' },
    caption: { fontSize: '12px', color: tokens.color.textSecondary },
  },
  shape: { borderRadius: tokens.radius.lg },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.color.bg,
          color: tokens.color.textPrimary,
          fontFeatureSettings: '"cv11", "ss01"',
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-track': { background: tokens.color.bg },
        '::-webkit-scrollbar-thumb': { background: tokens.color.border, borderRadius: 5 },
        '::-webkit-scrollbar-thumb:hover': { background: tokens.color.textMuted },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          padding: '8px 16px',
        },
        containedPrimary: {
          backgroundColor: tokens.color.accent,
          '&:hover': { backgroundColor: tokens.color.accentHover },
        },
        outlined: {
          borderColor: tokens.color.border,
          color: tokens.color.textPrimary,
          '&:hover': {
            borderColor: tokens.color.accent,
            backgroundColor: tokens.color.accentMuted,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface,
          border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.lg,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface,
          backgroundImage: 'none',
          border: `1px solid ${tokens.color.border}`,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.surface2,
          '& fieldset': { borderColor: tokens.color.border },
          '&:hover fieldset': { borderColor: tokens.color.textMuted },
          '&.Mui-focused fieldset': { borderColor: tokens.color.accent },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 10, 15, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${tokens.color.border}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
          fontWeight: 500,
          fontSize: '12px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.color.border}` },
        head: {
          color: tokens.color.textSecondary,
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.color.border } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.color.surface2,
          border: `1px solid ${tokens.color.border}`,
          fontSize: '12px',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
